import { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
    VIEW_ATTENDANCE_INNER_COLUMNS,
    VIEW_ATTENDANCE_OUTER_COLUMNS,
    getPunchHistoryForRow,
    getViewAttendanceExportDateTime,
    loadAllPunchHistory,
    mapPunchRowsForExport,
    mapViewAttendanceOuterRowsForExport,
} from "./attendanceViewExportHelpers";

const OUTER_HEAD_STYLES = {
    fillColor: [0, 51, 102],
    textColor: [255, 255, 255],
    fontSize: 7,
    halign: "center",
};

const OUTER_BODY_STYLES = {
    fontSize: 6,
    halign: "center",
};

const INNER_HEAD_STYLES = {
    fillColor: [219, 234, 254],
    textColor: [0, 51, 102],
    fontSize: 7,
    halign: "center",
};

const INNER_BODY_STYLES = {
    fontSize: 6,
    halign: "center",
};

const getStartY = (doc) => doc.lastAutoTable?.finalY ?? 35;

const addPageIfNeeded = (doc, startY, minSpace = 30) => {
    const pageHeight = doc.internal.pageSize.height;
    if (startY + minSpace > pageHeight - 14) {
        doc.addPage();
        return 20;
    }
    return startY;
};

export const ExportViewAttendancePdf = ({ attendanceData = [], fetchExportData }) => {
    const [isExporting, setIsExporting] = useState(false);

    const exportToPDF = async () => {
        setIsExporting(true);

        try {
            const rows = fetchExportData ? await fetchExportData() : attendanceData;
            const punchHistoryByEmployeeId = await loadAllPunchHistory(rows);
            const outerRows = mapViewAttendanceOuterRowsForExport(rows);
            const doc = new jsPDF({ orientation: "landscape" });
            const pageWidth = doc.internal.pageSize.width;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(16);
            doc.text("View Attendance Report", pageWidth / 2, 20, { align: "center" });
            doc.setFontSize(10);
            doc.text(getViewAttendanceExportDateTime(), pageWidth / 2, 28, {
                align: "center",
            });

            let startY = 35;

            outerRows.forEach((outerRow, index) => {
                const row = rows[index];
                const punches = getPunchHistoryForRow(row, punchHistoryByEmployeeId);

                startY = addPageIfNeeded(doc, startY, 12);

                autoTable(doc, {
                    startY,
                    head: index === 0 ? [VIEW_ATTENDANCE_OUTER_COLUMNS] : [],
                    body: [outerRow],
                    headStyles: OUTER_HEAD_STYLES,
                    bodyStyles: OUTER_BODY_STYLES,
                    margin: { left: 14, right: 14 },
                });

                startY = getStartY(doc) + 4;

                if (!punches.length) return;

                startY = addPageIfNeeded(doc, startY, 24);

                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                doc.setFillColor(219, 234, 254);
                doc.rect(14, startY - 5, pageWidth - 28, 8, "F");
                doc.setTextColor(0, 51, 102);
                doc.text(
                    `Punch History - ${row.employeeCode ?? row.empCode ?? "N/A"}`,
                    18,
                    startY
                );
                doc.setTextColor(0, 0, 0);

                autoTable(doc, {
                    startY: startY + 3,
                    head: [VIEW_ATTENDANCE_INNER_COLUMNS],
                    body: mapPunchRowsForExport(punches),
                    headStyles: INNER_HEAD_STYLES,
                    bodyStyles: INNER_BODY_STYLES,
                    margin: { left: 22, right: 14 },
                });

                startY = getStartY(doc) + 8;
            });

            doc.save(`ViewAttendanceReport_${new Date().toISOString().split("T")[0]}.pdf`);
        } catch (error) {
            console.error("Error generating PDF file:", error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <button
            type="button"
            onClick={exportToPDF}
            className="button-container ms-0"
            disabled={isExporting || (!fetchExportData && attendanceData.length === 0)}
        >
            <i className="fa-solid fa-file-pdf me-1 icon-container"></i>
            {isExporting ? "" : "PDF"}
        </button>
    );
};
